import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, brl, errToast } from "@/lib/api";
import { imprimirConferencia } from "@/lib/conferencia";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Printer, Users, User } from "lucide-react";

const VIA = {
  agente: "Enviada para a impressora do caixa",
  qz: "Impressa na impressora do caixa",
};

/**
 * Conferência de consumo (pré-conta) — o garçom escolhe entre a mesa inteira
 * e a parte de uma pessoa. O total mostrado vem do backend, pela mesma regra
 * do papel impresso (gorjeta já somada).
 */
export default function ConferenciaDialog({ open, onClose, tab }) {
  const [escopo, setEscopo] = useState("mesa");
  const [selected, setSelected] = useState({});
  const [pagador, setPagador] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewErro, setPreviewErro] = useState(null);
  const [busy, setBusy] = useState(false);
  // Impressão pelo navegador só depois que este diálogo sai da tela. A janela
  // de impressão congela a página: aberta no meio da animação de fechar, a
  // animação nunca terminava e o diálogo ficava preso, sem X nem Voltar.
  const imprimirDepois = useRef(null);
  const dispararImpressao = () => {
    const fn = imprimirDepois.current;
    imprimirDepois.current = null;
    if (fn) fn();
  };

  useEffect(() => {
    if (open) { setEscopo("mesa"); setSelected({}); setPagador(""); }
  }, [open, tab?.id]);

  const unpaid = (tab?.items || []).filter(i => !i.paid);
  const selIds = unpaid.filter(i => selected[i.id]).map(i => i.id);
  const body = escopo === "mesa" ? {} : { item_ids: selIds, pagador: pagador.trim() || null };
  const bodyKey = JSON.stringify(body);

  useEffect(() => {
    if (!open || !tab?.id) return;
    if (escopo === "pessoa" && selIds.length === 0) { setPreview(null); setPreviewErro(null); return; }
    let vivo = true;
    const h = setTimeout(() => {
      api.post(`/tabs/${tab.id}/conferencia`, body)
        .then(({ data }) => { if (vivo) { setPreview(data); setPreviewErro(null); } })
        .catch(e => {
          if (!vivo) return;
          setPreview(null);
          const d = e?.response?.data?.detail;
          setPreviewErro(typeof d === "string" ? d : "Não foi possível calcular a conferência.");
        });
    }, 250);
    return () => { vivo = false; clearTimeout(h); };
  }, [open, tab?.id, tab?.items, bodyKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const imprimir = async () => {
    setBusy(true);
    try {
      const r = await imprimirConferencia(tab.id, body);
      if (r.imprimir) {
        toast.warning(`Não deu para imprimir direto: ${r.motivo} Abrindo a impressão do navegador.`,
                      { duration: 10000 });
        imprimirDepois.current = r.imprimir;
        // Rede de segurança caso o fim da animação não seja avisado.
        setTimeout(dispararImpressao, 1000);
      } else {
        toast.success(`${VIA[r.via]}: total${brl(r.total)}`);
      }
      onClose();
    } catch (e) {
      errToast(e, "Erro ao imprimir a conferência");
    } finally {
      setBusy(false);
    }
  };

  if (!tab) return null;
  const podeImprimir = !busy && !previewErro && (escopo === "mesa" || selIds.length > 0);

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      {/* Cores explícitas: o diálogo abre também sobre o app escuro do garçom. */}
      <DialogContent className="max-w-md bg-white text-neutral-900" data-testid="conferencia-dialog"
                     onCloseAutoFocus={e => {
                       if (imprimirDepois.current) { e.preventDefault(); dispararImpressao(); }
                     }}>
        <DialogHeader>
          <DialogTitle>Imprimir conferência: {tab.label}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">
          Para o cliente conferir o consumo antes de pagar. Não é documento fiscal e não fecha a comanda.
        </p>

        <div className="grid grid-cols-2 gap-2">
          <Button type="button" variant={escopo === "mesa" ? "default" : "outline"}
                  onClick={() => setEscopo("mesa")} data-testid="conferencia-escopo-mesa">
            <Users className="w-4 h-4 mr-1" /> Mesa inteira
          </Button>
          <Button type="button" variant={escopo === "pessoa" ? "default" : "outline"}
                  onClick={() => setEscopo("pessoa")} data-testid="conferencia-escopo-pessoa">
            <User className="w-4 h-4 mr-1" /> Parte de uma pessoa
          </Button>
        </div>

        {escopo === "pessoa" && (
          <div className="space-y-2">
            <div>
              <Label htmlFor="conferencia-pagador" className="text-xs">Nome (opcional)</Label>
              <Input id="conferencia-pagador" value={pagador} onChange={e => setPagador(e.target.value)}
                     placeholder="Ex.: João" className="h-9" data-testid="conferencia-pagador" />
            </div>
            <div className="max-h-60 overflow-y-auto border rounded-md divide-y" data-testid="conferencia-itens">
              {unpaid.map(i => (
                <label key={i.id} className="flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer">
                  <Checkbox checked={!!selected[i.id]}
                            onCheckedChange={v => setSelected(s => ({ ...s, [i.id]: !!v }))}
                            data-testid={`conferencia-item-${i.id}`} />
                  <span className="flex-1 truncate">{i.quantity}x {i.name}</span>
                  <span className="tabular-nums">{brl(i.subtotal)}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="rounded-md bg-muted/50 p-3 text-sm space-y-1" data-testid="conferencia-preview">
          {previewErro ? (
            <p className="text-rose-700 text-xs">{previewErro}</p>
          ) : !preview ? (
            <p className="text-xs text-muted-foreground">
              {escopo === "pessoa" && selIds.length === 0 ? "Selecione os itens da pessoa." : "Calculando…"}
            </p>
          ) : (
            <>
              <div className="flex justify-between"><span>Subtotal</span><span className="tabular-nums">{brl(preview.subtotal)}</span></div>
              {preview.gorjeta > 0 && (
                <div className="flex justify-between"><span>Gorjeta ({preview.gorjeta_percentual}%)</span><span className="tabular-nums">{brl(preview.gorjeta)}</span></div>
              )}
              <div className="flex justify-between font-bold text-base"><span>Total</span><span className="tabular-nums" data-testid="conferencia-total">{brl(preview.total)}</span></div>
              {preview.ja_pago > 0 && (
                <div className="text-xs text-muted-foreground">Já pago nesta comanda: {brl(preview.ja_pago)} (fora do total)</div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Voltar</Button>
          <Button onClick={imprimir} disabled={!podeImprimir} data-testid="conferencia-imprimir">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Printer className="w-4 h-4 mr-1" />}
            Imprimir conferência
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
